import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import financeService from '../services/financeService';
import categoriesService from '../services/categoriesService';
import { toast } from 'react-hot-toast';
import { FolderOpen, Plus, Edit, Trash2, X, Save, Package, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import ConfirmModal from './ConfirmModal';

const TYPE_LABELS = {
  'standard': 'Padrão',
  'unit': 'Unidade',
  'piece': 'Peça',
  'subscription': 'Assinatura',
  'service': 'Serviço',
  'volume': 'Volume',
  'distance': 'Distância',
  'area': 'Área',
  'time': 'Tempo',
  'kg': 'Quilograma',
  'liter': 'Litro',
  'meter': 'Metro',
};

const FinanceCategoriesManager = () => {
  const [activeTab, setActiveTab] = useState('cost');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', type: 'VARIAVEL', ativo: true });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [confirmAction, setConfirmAction] = useState(null);
  const queryClient = useQueryClient();

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setSearchTerm('');
  };

  // Cost Categories Query
  const { data: costCategoriesData, isLoading: costLoading } = useQuery({
    queryKey: ['cost-categories', { search: searchTerm }],
    queryFn: () => categoriesService.getAllCategories(),
    enabled: activeTab === 'cost'
  });

  // Expense Categories Query
  const { data: expenseCategoriesData, isLoading: expenseLoading } = useQuery({
    queryKey: ['finance-expense-categories', { search: searchTerm }],
    queryFn: () => financeService.getExpenseCategories(),
    enabled: activeTab === 'expense'
  });

  const costCategories = costCategoriesData?.data || [];
  const expenseCategories = expenseCategoriesData?.data || [];

  const filteredCategories = (activeTab === 'cost' ? costCategories : expenseCategories).filter(cat =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filteredCategories.length / limit));
  const startIndex = (page - 1) * limit;
  const paginatedCategories = filteredCategories.slice(startIndex, startIndex + limit);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  // Cost Categories Mutations
  const costSaveMutation = useMutation({
    mutationFn: ({ id, ...data }) => id
      ? categoriesService.updateCategory(id, data)
      : categoriesService.createCategory(data),
    onSuccess: () => {
      toast.success(editingItem ? 'Categoria atualizada!' : 'Categoria criada!');
      queryClient.invalidateQueries(['cost-categories']);
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao salvar');
    }
  });

  const costDeactivateMutation = useMutation({
    mutationFn: ({ id, ...data }) => categoriesService.updateCategory(id, data),
    onSuccess: () => {
      toast.success('Categoria desativada!');
      queryClient.invalidateQueries(['cost-categories']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao desativar');
    }
  });

  const costDeleteMutation = useMutation({
    mutationFn: (id) => categoriesService.deleteCategory(id),
    onSuccess: () => {
      toast.success('Categoria removida!');
      queryClient.invalidateQueries(['cost-categories']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao remover');
    }
  });

  // Expense Categories Mutations
  const expenseSaveMutation = useMutation({
    mutationFn: ({ id, ...data }) => id
      ? financeService.updateExpenseCategory(id, data)
      : financeService.createExpenseCategory(data),
    onSuccess: () => {
      toast.success(editingItem ? 'Categoria atualizada!' : 'Categoria criada!');
      queryClient.invalidateQueries(['finance-expense-categories']);
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao salvar');
    }
  });

  const expenseDeactivateMutation = useMutation({
    mutationFn: ({ id, ...data }) => financeService.updateExpenseCategory(id, data),
    onSuccess: () => {
      toast.success('Categoria desativada!');
      queryClient.invalidateQueries(['finance-expense-categories']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao desativar');
    }
  });

  const expenseDeleteMutation = useMutation({
    mutationFn: (id) => financeService.deleteExpenseCategory(id),
    onSuccess: () => {
      toast.success('Categoria removida!');
      queryClient.invalidateQueries(['finance-expense-categories']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao remover');
    }
  });

  const openModal = (item = null, type = null) => {
    const tab = type || activeTab;
    setActiveTab(tab);
    if (item) {
      setEditingItem(item);
      if (tab === 'expense') {
        setFormData({ name: item.name, type: item.type || 'VARIAVEL', ativo: item.ativo !== false });
      } else {
        setFormData({
          name: item.name,
          description: item.description || '',
          unit_type: item.unit_type || 'standard',
          cost_scope: item.cost_scope || 'EMPRESA',
          ativo: item.ativo !== false
        });
      }
    } else {
      setEditingItem(null);
      if (tab === 'expense') {
        setFormData({ name: '', type: 'VARIAVEL', ativo: true });
      } else {
        setFormData({ name: '', description: '', unit_type: 'standard', cost_scope: 'EMPRESA', ativo: true });
      }
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setFormData({ name: '', type: 'VARIAVEL', ativo: true });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (activeTab === 'expense') {
      if (editingItem) {
        expenseSaveMutation.mutate({ id: editingItem.id, name: formData.name, type: formData.type, ativo: formData.ativo });
      } else {
        expenseSaveMutation.mutate(formData);
      }
    } else {
      if (editingItem) {
        costSaveMutation.mutate({ id: editingItem.id, ...formData });
      } else {
        costSaveMutation.mutate(formData);
      }
    }
  };

  const handleDelete = (id) => {
    const category = categories.find(c => c.id === id);
    const message = category
      ? `Deseja realmente desativar a categoria "${category.name}"?\n\nEla não será mais exibida nas listas, mas os dados históricos serão preservados.`
      : 'Deseja realmente desativar esta categoria?';

    setConfirmAction({
      message,
      onConfirm: () => {
        if (activeTab === 'expense') {
          expenseDeactivateMutation.mutate({ id, is_active: false });
        } else {
          costDeactivateMutation.mutate({ id, is_active: false });
        }
        setConfirmAction(null);
      },
      onCancel: () => setConfirmAction(null)
    });
  };

  const isLoading = activeTab === 'cost' ? costLoading : expenseLoading;
  const categories = activeTab === 'cost' ? costCategories : expenseCategories;

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categorias</h1>
          <p className="text-gray-500 mt-1">Gerencie as categorias de custos e despesas</p>
        </div>
        <button
          onClick={() => openModal(null)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nova Categoria
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => handleTabChange('cost')}
          className={`pb-3 px-2 font-medium text-sm transition-colors flex items-center gap-2 ${
            activeTab === 'cost'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Package className="w-4 h-4" />
          Custos Operacionais ({costCategories.length})
        </button>
        <button
          onClick={() => handleTabChange('expense')}
          className={`pb-3 px-2 font-medium text-sm transition-colors flex items-center gap-2 ${
            activeTab === 'expense'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <FolderOpen className="w-4 h-4" />
          Despesas Financeiras ({expenseCategories.length})
        </button>
      </div>

      {activeTab === 'cost' && (
        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Custos Operacionais:</strong> Categorias para classificar custos como Alimentação, Aluguel, Combustível, Insumos, etc.
          </p>
        </div>
      )}

      {activeTab === 'expense' && (
        <div className="mb-4 p-4 bg-purple-50 rounded-lg">
          <p className="text-sm text-purple-800">
            <strong>Despesas Financeiras:</strong> Categorias para despesas fixas e variáveis do módulo financeiro.
          </p>
        </div>
      )}

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Buscar por nome..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-gray-200 border-t-primary-500 rounded-full animate-spin" />
          <p className="text-gray-500 mt-3">Carregando...</p>
        </div>
      ) : paginatedCategories.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <FolderOpen className="w-8 h-8 text-primary-300" />
          </div>
          <p className="text-gray-500 font-medium">Nenhuma categoria encontrada</p>
          <p className="text-gray-400 text-sm mt-1">{searchTerm ? 'Tente buscar com outros termos' : 'Clique em "Nova Categoria" para começar'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-primary-50/50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                <th className="px-6 py-3">Nome</th>
                <th className="px-4 py-3">Tipo</th>
                {activeTab === 'cost' && <th className="px-4 py-3">Destino (Aba)</th>}
                {activeTab === 'cost' && <th className="px-4 py-3">Custos</th>}
                <th className="px-4 py-3 w-24">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedCategories.map((cat) => (
                <tr key={cat.id} className="hover:bg-primary-50/30 transition-colors">
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                      {cat.name}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">
                    {activeTab === 'expense' ? (
                      cat.type === 'FIXO' ? (
                        <span className="text-green-600">Fixo</span>
                      ) : (
                        <span className="text-orange-600">Variável</span>
                      )
                    ) : (
                      TYPE_LABELS[cat.unit_type] || cat.unit_type || '-'
                    )}
                  </td>
                  {activeTab === 'cost' && (
                    <>
                      <td className="px-4 py-4 text-xs font-semibold">
                        {cat.cost_scope === 'EQUIPE' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                            👷 Equipe
                          </span>
                        ) : cat.cost_scope === 'TRIBUTO' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                            🏛️ Tributos
                          </span>
                        ) : cat.cost_scope === 'TODAS' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                            🌐 Todas as Abas
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                            🏢 Empresa
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {cat.costs_count || 0}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openModal(cat)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(cat.id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remover"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredCategories.length > limit && (
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Itens por página:</span>
                <select
                  value={limit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                  className="p-1 border border-gray-300 rounded-lg text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  Página {page} de {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingItem ? 'Editar' : 'Nova'} Categoria
              </h2>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Ex: Alimentação, Aluguel"
                  required
                />
              </div>

              {activeTab === 'expense' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="VARIAVEL">Variável</option>
                    <option value="FIXO">Fixo</option>
                  </select>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Destino na Gestão de Custos (Em qual aba vai aparecer?) *
                    </label>
                    <select
                      value={formData.cost_scope || 'EMPRESA'}
                      onChange={(e) => setFormData({ ...formData, cost_scope: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-medium"
                      required
                    >
                      <option value="EMPRESA">🏢 Empresa (Custos Fixos / Operacionais da Empresa)</option>
                      <option value="EQUIPE">👷 Equipe (Alimentação, Transporte, Despesas de Colaboradores)</option>
                      <option value="TRIBUTO">🏛️ Tributos (FGTS, INSS, DARF, Impostos e Taxas)</option>
                      <option value="TODAS">🌐 Todas as Abas (Aparece em todos os formulários)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Define em qual formulário de lançamento de custo esta categoria ficará disponível.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Unidade</label>
                    <select
                      value={formData.unit_type || 'standard'}
                      onChange={(e) => setFormData({ ...formData, unit_type: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="standard">Padrão</option>
                      <option value="unit">Unidade</option>
                      <option value="piece">Peça</option>
                      <option value="kg">Quilograma</option>
                      <option value="liter">Litro</option>
                      <option value="meter">Metro</option>
                      <option value="service">Serviço</option>
                      <option value="subscription">Assinatura</option>
                    </select>
                  </div>
                </>
              )}

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={formData.ativo}
                  onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="ativo" className="text-sm font-medium text-gray-700">Categoria Ativa</label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmAction && (
        <ConfirmModal
          show={true}
          message={confirmAction.message}
          onConfirm={confirmAction.onConfirm}
          onCancel={confirmAction.onCancel}
        />
      )}
    </div>
  );
};

export default FinanceCategoriesManager;
